import type { PieArcLabelClasses, PieArcLabelProps, PieItemId } from '@mui/x-charts-pro';
import type { GetAppointmentsByTypeResponse } from '#services/api/insights.ts';

import * as React from 'react';
import { styled } from '@mend/mui';
import { Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import composeClasses from '@mui/utils/composeClasses';
import {
  ChartDataProvider,
  ChartsLegend,
  ChartsSurface,
  ChartsTooltip,
  getPieArcLabelUtilityClass,
  PiePlot,
  useDrawingArea,
} from '@mui/x-charts-pro';
import { useQuery } from '@tanstack/react-query';

import { appointmentsByType } from '#lib/react-query/queries.ts';
import {
  InfoList,
  InfoParagraph,
  InfoSection,
  InfoTemplate,
} from '#routes/Home/Widgets/InfoTemplateComponents.tsx';
import { CHART_SERIES_COLORS, WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { Base } from './Base';
import { EmptyDataOverlay } from './EmptyDataOverlay.tsx';
import { useAppointmentWidget } from './use-appointment-widget.ts';

interface PieArcLabelOwnerState {
  id: PieItemId;
  color: string;
  isFaded: boolean;
  isHighlighted: boolean;
  skipAnimation: boolean;
  classes?: Partial<PieArcLabelClasses>;
}

// Chart configuration constants
const CHART_CONFIG = {
  innerRadius: 70,
  outerRadius: 125,
  arcLabelRadius: 0,
  centerLabelOffset: 11,
  paddingAngle: 0,
} as const;

const widgetTitle = 'Appointments Type Breakdown';

const infoTemplate = (
  <InfoTemplate>
    <InfoSection title="What it is &amp; Problems it Solves">
      <InfoParagraph>
        Shows the distribution of appointments by base type (e.g., In-Person, Telehealth) for the
        selected time period. This helps organizations see how care is being delivered and compare
        demand across different appointment formats.
      </InfoParagraph>
    </InfoSection>

    <InfoSection title="How it Works (Calculation)" sx={{ mb: 2 }}>
      <InfoSection title="Count all appointments that are:" indented>
        <InfoList
          items={[
            'Not deleted, not canceled, and not marked as no-show',
            'Assigned to a valid base appointment type',
            'Grouped by base appointment type',
            'Total = sum of counts across all appointment types',
            'Percentages = represented automatically by the donut chart',
          ]}
        />
      </InfoSection>

      <InfoSection title="Includes" indented>
        <InfoList
          items={[
            'Appointments with a valid base appointment type mapping',
            'Valid appointment types: Video, Group Video, Office Video, Phone, Group Phone, In Office, Group In Office',
          ]}
        />
      </InfoSection>

      <InfoSection title="Excludes" indented>
        <InfoList
          items={[
            'Deleted appointments',
            'Canceled appointments',
            'No-show appointments',
            'Messaging appointment types',
            'Appointments without a valid type mapping (rare)',
          ]}
        />
      </InfoSection>

      <InfoSection title="Notes &amp; Caveats" indented>
        <InfoList
          items={[
            'This widget measures volume and proportion only—does not track confirmation, completion, or no-show outcomes',
            'Percentages are not explicitly calculated in the data but are displayed by the chart visualization',
            'Unutilized appointment types, or appointment types filtered out with dashboard filters, will not display in the donut chart or the legend',
            'Data updates nightly, so very recent changes may not yet appear.',
          ]}
        />
      </InfoSection>
    </InfoSection>
  </InfoTemplate>
);

const StyledText = styled('text')(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: 'middle',
  dominantBaseline: 'central',
}));

function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  // Using centerLabelOffset to align with the center of the donut for 2 text lines
  return (
    <>
      <StyledText
        x={left + width / 2}
        y={top + height / 2 - CHART_CONFIG.centerLabelOffset}
        fontSize={20}
      >
        {children}
      </StyledText>
      <StyledText
        x={left + width / 2}
        y={top + height / 2 + CHART_CONFIG.centerLabelOffset}
        fontSize={12}
      >
        Total
      </StyledText>
    </>
  );
}

interface PieArcLabelOwnerState {
  id: PieItemId;
  color: string;
  isFaded: boolean;
  isHighlighted: boolean;
  skipAnimation: boolean;
  classes?: Partial<PieArcLabelClasses>;
}

const useUtilityClasses = (ownerState: PieArcLabelOwnerState) => {
  const { classes, id, isFaded, isHighlighted, skipAnimation } = ownerState;
  const slots = {
    root: [
      'root',
      `series-${id}`,
      isHighlighted && 'highlighted',
      isFaded && 'faded',
      !skipAnimation && 'animate',
    ],
  };

  return composeClasses(slots, getPieArcLabelUtilityClass, classes);
};

const PieArcLabel = React.forwardRef<SVGTextElement, PieArcLabelProps>(
  function PieArcLabel(props, ref) {
    const theme = useTheme();
    const {
      id,
      classes: innerClasses,
      color,
      startAngle,
      endAngle,
      innerRadius,
      outerRadius,
      formattedArcLabel,
      isHighlighted,
      isFaded,
      skipAnimation,
      ...other
    } = props;

    const ownerState = {
      id,
      classes: innerClasses,
      color,
      isFaded,
      isHighlighted,
      skipAnimation,
    };
    const classes = useUtilityClasses(ownerState);

    const centerAngleDeg = (startAngle + endAngle) / 2;
    const centerRadius = (innerRadius + outerRadius) / 2;
    const centerAngle = centerAngleDeg - Math.PI / 2;

    const x = Math.cos(centerAngle) * centerRadius;
    const y = Math.sin(centerAngle) * centerRadius;

    const fillLabelText = theme.palette.getContrastText(color);

    return (
      <text
        ref={ref}
        className={classes.root}
        {...other}
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fill={fillLabelText}
      >
        {formattedArcLabel}
      </text>
    );
  }
);

const selectData = (response: GetAppointmentsByTypeResponse) => {
  const aggregatedData = response.data.reduce(
    (acc, item) => {
      acc.telehealthAppointments += item.telehealthAppointments;
      acc.officeVideoAppointments += item.officeVideoAppointments;
      acc.phoneAppointments += item.phoneAppointments;
      acc.inPersonAppointments += item.inPersonAppointments;
      return acc;
    },
    {
      telehealthAppointments: 0,
      officeVideoAppointments: 0,
      phoneAppointments: 0,
      inPersonAppointments: 0,
    }
  );

  const data = [
    {
      id: 0,
      value: aggregatedData.telehealthAppointments,
      label: 'Telehealth',
    },
    {
      id: 1,
      value: aggregatedData.officeVideoAppointments,
      label: 'Office Video',
    },
    { id: 2, value: aggregatedData.phoneAppointments, label: 'Phone' },
    {
      id: 3,
      value: aggregatedData.inPersonAppointments,
      label: 'In-Person',
    },
  ].filter((item) => item.value > 0);

  const totalAppointments = data.reduce((sum, item) => sum + item.value, 0);

  return { ...response, data, totalAppointments };
};

/**
 * Disable the default axis behavior to center the chart when using composition.
 *
 * @see https://mui.com/x/react-charts/pie/#composition
 */
const noAxis = [{ position: 'none' }] as const;

/**
 * TODO: While the chart's container fills up the space in the dialog, the
 * donut itself does not.
 */
export function AggregatedAppointmentsByType() {
  const widget = useAppointmentWidget();

  const theme = useTheme();

  const { data, isLoading } = useQuery({
    ...appointmentsByType(widget.getCommonRequestParams()),
    select: selectData,
  });

  if (isLoading) {
    return (
      <Base title={widgetTitle}>
        <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />
      </Base>
    );
  }

  return (
    <Base
      id="appointments-type-breakdown"
      title={widgetTitle}
      infoTemplate={infoTemplate}
      drilldownRoutePath={widget.getDrilldownRoutePath(true)}
    >
      {(isOpenedInDialog) => (
        <Box height="100%">
          {data && data.totalAppointments > 0 ? (
            <ChartDataProvider
              colors={CHART_SERIES_COLORS[theme.palette.mode]}
              height={isOpenedInDialog ? undefined : WIDGET_CHART_HEIGHT}
              series={[
                {
                  data: data.data,
                  innerRadius: CHART_CONFIG.innerRadius,
                  paddingAngle: CHART_CONFIG.paddingAngle,
                  outerRadius: CHART_CONFIG.outerRadius,
                  arcLabel: (item) => `${item.value}`,
                  arcLabelMinAngle: 20,
                  arcLabelRadius: CHART_CONFIG.arcLabelRadius,
                  type: 'pie',
                },
              ]}
              xAxis={noAxis}
              yAxis={noAxis}
            >
              <ChartsLegend
                sx={{
                  justifyContent: 'center',
                  '.MuiChartsLegend-label': {
                    color: theme.palette.muted[4],
                  },
                }}
              />
              <ChartsSurface>
                <PiePlot slots={{ pieArcLabel: PieArcLabel }} />
                <ChartsTooltip trigger="item" />
                <PieCenterLabel>{data.totalAppointments}</PieCenterLabel>
              </ChartsSurface>
            </ChartDataProvider>
          ) : (
            <EmptyDataOverlay variant={isOpenedInDialog ? 'dialog' : 'chart'} />
          )}
        </Box>
      )}
    </Base>
  );
}
